import { useEffect, useRef, useState } from 'react';

import { useAtomsToastManager } from '@opendoor/atoms/toast';
import { useAuth } from '@opendoor/auth-fe/context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { impersonationSession } from 'components/account/impersonation';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { BuyerPurchaseOfferSetHiddenDocument } from 'components/hub/__generated__/purchase-offer-visibility.zeus';
import type { HubPurchasePage } from 'components/hub/useHubPurchasePage';
import { useHubPurchasePage } from 'components/hub/useHubPurchasePage';
import { isActiveBuyerOffer, isInContract } from 'helpers/buyerOfferStatus';
import { captureError } from 'helpers/captureError';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';

import { purchasePortfolioRows } from './homePortfolioRows';
import { HomePortfolioTable } from './HomePortfolioTable';
import type { HomePortfolioRow } from './HomePortfolioTable';

const invalidPageErrorSchema = z.object({
  extensions: z.object({ code: z.literal('BAD_USER_INPUT') }),
});

const submittedDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const closingDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

function purchaseKeyDate(offer: HubPurchasePage['rows'][number]['offer']): string | null {
  if (!offer) return null;
  const inContract = isInContract(offer.transactionState);
  if (!inContract && !isActiveBuyerOffer(offer.transactionState)) return null;
  const date = inContract ? offer.closeOfEscrowDate : offer.submittedAt;
  if (!date) return null;
  const formatter = inContract ? closingDateFormatter : submittedDateFormatter;
  return `${inContract ? 'Closes' : 'Submitted'} ${formatter.format(new Date(date))}`;
}

function purchasePageRows(page: HubPurchasePage | undefined): HomePortfolioRow[] {
  return (page?.rows ?? []).flatMap(({ id, clients, offer, isHidden }) => {
    const fallback = {
      id,
      transactionState: 'ACTIVE' as const,
      priceCents: null,
      listing: null,
      taskGroups: [],
    };
    return purchasePortfolioRows([offer ?? fallback]).map((row) => ({
      ...row,
      clients: [...new Set(clients.map(({ name }) => name))],
      detailsUnavailable: !offer,
      keyDate: purchaseKeyDate(offer),
      visibility: {
        isHidden,
        canHide:
          !!offer && offer.transactionState !== 'UNKNOWN' && !isInContract(offer.transactionState),
      },
      transaction: offer
        ? row.transaction
        : { ...row.transaction, statusLabel: 'Details unavailable' },
    }));
  });
}

export function PurchasePortfolioTable() {
  const [filters, setFilters] = useState({
    activeTab: true,
    search: '',
    page: 0,
    cursors: [null] as (string | null)[],
  });
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticated' ? authentication.user.customerUuid : null;
  const currentCustomer = useRef(customerUuid);
  useEffect(() => {
    currentCustomer.current = customerUuid;
    return () => {
      currentCustomer.current = null;
    };
  }, [customerUuid]);
  const queryClient = useQueryClient();
  const toasts = useAtomsToastManager();
  const visibility = useMutation({
    scope: { id: `purchase-visibility-${customerUuid}` },
    retry: false,
    mutationFn: async (change: {
      id: string;
      street: string;
      isHidden: boolean;
      customerUuid: string;
    }) => {
      if (currentCustomer.current !== change.customerUuid)
        throw new Error('The signed-in account changed.');
      const response = await requestZeusFromBrowser(BuyerPurchaseOfferSetHiddenDocument, {
        input: { offerId: change.id, isHidden: change.isHidden },
      });
      const result = response.buyerPurchaseOfferSetHidden;
      if (result.__typename !== 'PurchaseOfferDashboardState') throw new Error(result.message);
      return result;
    },
    onSuccess: async (_result, change) => {
      const queryKey = ['hub', 'purchase-page', change.customerUuid];
      await queryClient.cancelQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      if (currentCustomer.current !== change.customerUuid) return;
      setFilters((current) => ({ ...current, page: 0, cursors: [null] }));
      toasts.add({
        title: change.isHidden
          ? `${change.street} is hidden. Find it in Other.`
          : `${change.street} is no longer hidden.`,
        timeout: 8000,
        actions: change.isHidden
          ? [
              {
                children: 'Undo',
                'aria-label': 'Undo',
                analyticsName: 'home-purchase-portfolio-hide-undo',
                variant: 'secondary',
                onClick: () => {
                  if (currentCustomer.current === change.customerUuid)
                    visibility.mutate({ ...change, isHidden: false });
                },
              },
            ]
          : undefined,
      });
    },
    onError: (error, change) => {
      if (currentCustomer.current !== change.customerUuid) return;
      captureError(error, 'PurchaseOfferVisibility');
      toasts.add({
        variant: 'error',
        title: `We couldn’t ${change.isHidden ? 'hide' : 'unhide'} this offer. Try again.`,
      });
    },
  });
  const [search] = useDebounce(filters.search.trim(), 300);
  const debouncing = search !== filters.search.trim();
  const after = filters.cursors[filters.page] ?? null;
  const includeHomeSummary = filters.activeTab && search === '' && after === null;
  const query = useHubPurchasePage({
    variables: {
      segment: filters.activeTab ? 'ACTIVE' : 'OTHER',
      search,
      after,
      includeHomeSummary,
    },
    enabled: !debouncing && !visibility.isPending,
    retainPreviousPage: !visibility.isPending,
    refetchOnMount: false,
  });
  const data = debouncing ? undefined : query.data?.page;
  const displayedPage = query.data
    ? Math.max(0, filters.cursors.indexOf(query.data.cursor))
    : filters.page;
  const reset = (activeTab: boolean, search: string) =>
    setFilters({ activeTab, search, page: 0, cursors: [null] });
  return (
    <HomePortfolioTable
      section="home-purchase"
      title="Home purchases"
      showClientColumn
      showKeyDateColumn
      searchScope="address-or-client"
      searchCoverage="portfolio"
      searchMaxLength={256}
      rows={purchasePageRows(data)}
      isUpdatingVisibility={visibility.isPending}
      onVisibilityChange={
        !impersonationSession.isImpersonatingSession() && customerUuid
          ? (row, isHidden) => {
              visibility.mutate({
                id: row.transaction.id,
                street: row.street,
                isHidden,
                customerUuid,
              });
            }
          : undefined
      }
      action={{ label: 'Browse homes', href: '/homes' }}
      isLoading={debouncing || query.isPending}
      isRefreshing={!!data && query.isFetching}
      hasError={query.isError}
      hasPartialError={data?.rows.some(({ offer }) => !offer)}
      onRetry={() => {
        if (filters.page > 0 && invalidPageErrorSchema.safeParse(query.error).success) {
          reset(filters.activeTab, filters.search);
          return;
        }
        void query.refetch();
      }}
      pageControls={{
        activeTab: filters.activeTab,
        search: filters.search,
        page: displayedPage,
        counts: data
          ? { total: data.totalCount, active: data.activeCount, other: data.otherCount }
          : undefined,
        hasNextPage: data?.pageInfo.hasNextPage ?? false,
        onTabChange: (active) => reset(active, filters.search),
        onSearchChange: (search) => reset(filters.activeTab, search),
        onPrevious: () =>
          setFilters((current) => ({ ...current, page: Math.max(0, current.page - 1) })),
        onNext: () => {
          const cursor = data?.pageInfo.endCursor;
          if (!cursor || !data?.pageInfo.hasNextPage) return;
          setFilters((current) => ({
            ...current,
            page: current.page + 1,
            cursors: [...current.cursors.slice(0, current.page + 1), cursor],
          }));
        },
      }}
    />
  );
}
